import { styled } from "goober";
import { useCallback, useEffect, useRef, useState } from "react";
import { Binary } from "./Binary";
import { Icon } from "../Icon";
import { PressButton } from "../PressButton";
import { Text } from "../Text";
import { extensionFromMime } from "./download";
import { DownloadButton } from "./DownloadButton";
import type { FileRenderControl } from "./types";
import { useFileActions } from "./useFileActions";
import { useObjectUrl } from "./useObjectUrl";

const Row = styled("div")`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 12px;
  height: 48px;
  background: var(--surface-primary);
  border-radius: 12px;
  border: 0.5px solid var(--border-alpha);
`;

const PlayButton = styled(PressButton)`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  flex-shrink: 0;
  border: none;
  border-radius: 50%;
  background: var(--text-primary);
  color: var(--surface-primary);
  cursor: pointer;
  padding: 0;

  &:hover {
    background: var(--text-secondary);
  }
`;

const Track = styled("div")`
  position: relative;
  flex: 1;
  min-width: 60px;
  height: 24px;
  cursor: pointer;
  display: flex;
  align-items: center;
`;

const TrackLine = styled("div")`
  position: relative;
  width: 100%;
  height: 4px;
  background: var(--alpha8);
  border-radius: 2px;
`;

const Fill = styled("div")`
  position: absolute;
  inset: 0 auto 0 0;
  background: var(--text-primary);
  border-radius: 2px;
`;

const HoverFill = styled("div")`
  position: absolute;
  inset: 0 auto 0 0;
  background: var(--alpha16);
  border-radius: 2px;
  pointer-events: none;
`;

const HandleHit = styled("div")`
  position: absolute;
  top: 50%;
  width: 16px;
  height: 24px;
  transform: translate(-50%, -50%);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: grab;

  &:active {
    cursor: grabbing;
  }
`;

const Handle = styled("div")`
  width: 4px;
  height: 12px;
  border-radius: 2px;
  background: var(--text-white);
  box-shadow: var(--elevation-low);
  outline: 0.5px solid var(--border-alpha);
  pointer-events: none;
`;

const TimestampTooltip = styled("div")`
  position: absolute;
  bottom: calc(50% + 10px);
  background: var(--tooltip-bg);
  color: var(--tooltip-fg);
  box-shadow: var(--elevation-low);
  padding: 4px 8px;
  border-radius: 999px;
  font-family: var(--font-mono);
  font-size: 10px;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
  transform: translateX(-50%);
  pointer-events: none;
`;

const Time = styled("span")`
  font-family: var(--font-mono);
  font-size: 11px;
  color: var(--text-secondary);
  font-variant-numeric: tabular-nums;
  flex-shrink: 0;
`;

type Props = {
  url?: string;
  contentType: string;
  fileName?: string;
  onControls?: (controls: FileRenderControl[]) => void;
};

function formatTime(seconds: number): string {
  const total = Math.max(0, Math.round(seconds));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
}

export function Audio({ url, contentType, fileName, onControls }: Props) {
  const audioRef = useRef<HTMLAudioElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);
  const { blobUrl, failed } = useObjectUrl(url);
  const [undecodableUrl, setUndecodableUrl] = useState<string>();
  const [duration, setDuration] = useState<number>();
  const [currentTime, setCurrentTime] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [hoverRatio, setHoverRatio] = useState<number | null>(null);
  const [dragging, setDragging] = useState(false);

  const togglePlay = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;
    if (audio.paused) {
      audio.play().catch((error: unknown) => {
        // interrupting before playback starts will reject. we catch to avoid the noise, like e.g.
        // https://github.com/signalapp/Signal-Desktop/blob/89f418b11feb94389999a268ec4cb1488308abc9/ts/services/globalMessageAudio.std.ts#L75-L80
        if (error instanceof DOMException && error.name === "AbortError")
          return;
        console.error("Audio playback failed:", error);
      });
    } else {
      audio.pause();
    }
  }, []);

  const ratioFromClientX = useCallback((clientX: number) => {
    const track = trackRef.current;
    if (!track) return null;
    const rect = track.getBoundingClientRect();
    return Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
  }, []);

  const seekToRatio = useCallback(
    (ratio: number) => {
      const audio = audioRef.current;
      if (!audio || !duration) return;
      audio.currentTime = ratio * duration;
    },
    [duration]
  );

  useEffect(() => {
    if (!dragging) return;
    const onMove = (e: MouseEvent) => {
      const ratio = ratioFromClientX(e.clientX);
      if (ratio === null) return;
      setHoverRatio(ratio);
      seekToRatio(ratio);
    };
    const onUp = () => setDragging(false);
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
    return () => {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
    };
  }, [dragging, ratioFromClientX, seekToRatio]);

  const ext = extensionFromMime(contentType);

  const downloadName = fileName ?? `audio.${ext}`;
  const { download, downloading } = useFileActions({
    url,
    fileName: downloadName,
    onControls,
  });

  if (failed)
    return (
      <Binary
        url={url}
        contentType={contentType}
        fileName={fileName}
        hideActions={!!onControls}
        note="Couldn’t load this file"
      />
    );

  if (!blobUrl) return null;

  if (undecodableUrl === blobUrl)
    return (
      <Binary
        url={url}
        contentType={contentType}
        fileName={fileName}
        hideActions={!!onControls}
        note="Can’t be played here"
      />
    );

  const progress = duration ? Math.min(1, currentTime / duration) : 0;

  return (
    <Row>
      <PlayButton onPress={togglePlay} aria-label={playing ? "Pause" : "Play"}>
        <Icon name={playing ? "pause" : "play"} size={12} />
      </PlayButton>
      <Track
        ref={trackRef}
        onMouseDown={(e) => {
          const ratio = ratioFromClientX(e.clientX);
          if (ratio === null) return;
          seekToRatio(ratio);
        }}
        onMouseMove={(e) => {
          if (dragging) return;
          const ratio = ratioFromClientX(e.clientX);
          if (ratio !== null) setHoverRatio(ratio);
        }}
        onMouseLeave={() => {
          if (!dragging) setHoverRatio(null);
        }}
      >
        <TrackLine>
          {hoverRatio !== null && (
            <HoverFill style={{ width: `${hoverRatio * 100}%` }} />
          )}
          <Fill style={{ width: `${progress * 100}%` }} />
        </TrackLine>
        <HandleHit
          style={{ left: `${progress * 100}%` }}
          onMouseDown={(e) => {
            e.stopPropagation();
            setDragging(true);
          }}
        >
          <Handle />
        </HandleHit>
        {hoverRatio !== null && duration != null && (
          <TimestampTooltip style={{ left: `${hoverRatio * 100}%` }}>
            {formatTime(hoverRatio * duration)}
          </TimestampTooltip>
        )}
      </Track>
      <Time>
        {formatTime(currentTime)} /{" "}
        {duration != null ? formatTime(duration) : "0:00"}
      </Time>
      {!onControls && (
        <DownloadButton onPress={download} disabled={!url || downloading}>
          <Icon name="arrow-down" size={12} />
          <Text variant="text-small-md">
            {downloading ? "Downloading…" : "Download"}
          </Text>
        </DownloadButton>
      )}
      <audio
        ref={audioRef}
        src={blobUrl}
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onTimeUpdate={(e) => setCurrentTime(e.currentTarget.currentTime)}
        onLoadedMetadata={(e) => {
          const d = e.currentTarget.duration;
          if (Number.isFinite(d)) setDuration(d);
        }}
        onEnded={() => setPlaying(false)}
        onError={() => setUndecodableUrl(blobUrl)}
        style={{ display: "none" }}
      />
    </Row>
  );
}
